// #4 — read-only QR board for an exhibition: every scannable code in the tree
// (the exhibition + each zone + the exhibits in those zones), grouped by zone,
// for production testing/printing. Display-only — QRs are generated on publish;
// items without one show a "publish to generate" note. Each card also shows the
// cover photo and an Open link to the entity's visitor page (the URL the QR
// encodes). Browser print works on the plain page.

import type { FC } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

import { assetUrl } from "@shared/api/assets";
import { Button } from "@shared/components/ui/button";
import {
  useExhibition,
  useZonesInExhibition,
} from "@shared/api/hooks/useExhibitions";
import { useExhibitionExhibits } from "@shared/api/hooks/useExhibits";

import { buildQrBoard, type QrItem } from "./qrBoard";
import QRCard from "./QRCard";

const GRID = "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4";

const cardFor = (item: QrItem) => (
  <QRCard
    key={item.id}
    kindLabel={item.kindLabel}
    title={item.title}
    code={item.code}
    qrCodeUrl={item.qrCodeUrl}
    coverImageUrl={item.coverImageId ? assetUrl(item.coverImageId) : null}
    openHref={`/${item.kind}/${item.id}`}
    testId={`qr-card-${item.kind}-${item.id}`}
  />
);

const ExhibitionQRBoard: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: exhibition } = useExhibition(id);
  const { data: zones = [] } = useZonesInExhibition(id);
  // F-R: exhibition-scoped rows carry the QR; the generic list no longer does.
  const { data: exhibits = [] } = useExhibitionExhibits(id);

  const board = buildQrBoard(exhibition, zones, exhibits);

  return (
    <div
      className="min-h-screen bg-background pb-12"
      data-testid="exhibition-qr-board"
    >
      <div className="bg-white border-b border-border">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-3">
          <Button
            onClick={() => navigate(`/staff/exhibition/${id}/edit`)}
            variant="ghost"
            size="sm"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-heading font-bold">
            QR codes{exhibition ? ` — ${exhibition.titleTc}` : ""}
          </h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-8">
        {board.exhibition && (
          <div className={GRID}>{cardFor(board.exhibition)}</div>
        )}

        {board.zones.map(({ zone, exhibits: zoneExhibits }) => (
          <div key={zone.id} className="space-y-3">
            <h2 className="text-lg font-heading font-semibold">
              Zone · {zone.title}{" "}
              <span className="text-sm font-normal text-muted-foreground">
                ({zone.code})
              </span>
            </h2>
            <div className={GRID}>
              {cardFor(zone)}
              {zoneExhibits.map(cardFor)}
            </div>
          </div>
        ))}

        {board.zones.length === 0 && (
          <p className="text-muted-foreground" data-testid="qr-board-empty">
            No zones in this exhibition yet.
          </p>
        )}
      </div>
    </div>
  );
};

export default ExhibitionQRBoard;
